﻿<script setup lang="ts">
    import { BatchDocumentCode, CodeEditContextRefCode, CodeEditContextRequest, getLibraries } from '../helpers/api';
    import { attributeTypeToStepSize, decodeIdBase64 } from '@/shared/helpers/utils';
    import { getCodeLink } from '../helpers/routing';
    import { computed, onMounted, Ref, ref } from 'vue';
    import { Library } from '@/shared/helpers/api/types/entities.ts';
    import { http } from '@/shared/httpWrapper';
    import {
        applyDynamicFilters,
        CodeRefChangedHandlers,
        CodeRefFilterFunctions,
        CodeRefSelectorCode,
        CodeRefValidationResult,
        CodeRefValidators,
        DynamicFilterPredicates,
        dynamicFilterBlamedFields,
        dynamicFilterHidesAllOptions,
        noDynamicFilterMatchMessage
    } from '@/shared/codeedit/referableCodeHandling.ts';
    import useCopyToClipboard from '../composables/useCopyToClipboard';
    import useShow from '../composables/useShow';
    import { choiceViolation, CodeEditFields } from '@/shared/codeedit/codeEditFields.ts';
    import { FieldDefinition, fieldDefinitionFormInputType, isHidden } from '@/shared/codeedit/field.ts';
    import { getNameLabel } from '@/shared/helpers/nameWithAliasHelper.ts';
    import { BulmaErrorClass, CodeEditSessionItem, ErrorType } from '@/shared/codeedit/codeEditSessionItem.ts';
    import EditSession from '@/shared/codeedit/EditSession.vue';
    import TypedBoolSelector from '@/shared/components/TypedBoolSelector.vue';
    import CodeRefSelector from '@/shared/components/CodeRefSelector.vue';
    import LibrarySelector from '@/shared/components/LibrarySelector.vue';
    import DateTimePicker from '@/shared/components/DateTimePicker.vue';
    import Spinner from '@/shared/components/Spinner.vue';
    import { CustomCommitCallback } from '@/shared/codeedit/editSessionTypes.ts';
    import { CodeEditSessionState } from '@/shared/codeedit/codeEditSessionState.ts';
    import { CodeTemplateValues } from '@/shared/codeedit/codeTemplate.ts';
    import GenericTextInput from '@/shared/components/GenericTextInput.vue';
    import MarkdownEditorModal from '@/shared/components/MarkdownEditorModal.vue';
    import { canEditCode } from '@/shared/directives/requirePermission';

    const canEditRelation = ref<Record<string, boolean>>({});
    const codeEditSessionState = new CodeEditSessionState();
    const libraries = ref<Library[]>([]);
    const loading = ref<boolean>(true);
    const copyToClipBoard = useCopyToClipboard();
    const relationSelectorKeys = ref<Record<string, number>>({});
    const show = useShow();

    const emit = defineEmits<{
        refresh: [payload: BatchDocumentCode[]];
        cancel: [];
        loaded: [];
    }>();

    export type ComputedField = {
        hasValue: true;
        label: string;
        value: string;
    } | {
        hasValue: false;
        label: string;
        value: null;
    };

    type Props = {
        beforeSaveCode?: (_code: CodeEditFields, _refCodes: Record<string, CodeEditContextRefCode[]>) => Promise<void>;
        codeId?: string;
        codeSetName?: string;
        codeTemplateValues?: CodeTemplateValues;
        computedFields?: (_modified: CodeEditFields) => ComputedField[] | undefined;
        customLabels?: Record<string, string>;
        fieldsToDisable?: string[];
        fieldsToIgnore?: string[];
        formTitle?: string;
        formTitleClass?: string;
        library: string;
        multipleCodeSetsToRelease?: string[];
        quickCommit?: boolean;
        refCodeChangedHandlers?: CodeRefChangedHandlers<CodeEditFields>;
        referableCodesFilters?: CodeRefFilterFunctions<CodeEditFields>;
        referableCodesValidators?: CodeRefValidators<CodeEditFields>;
        scopes?: string[];
        submitButtonLabel?: string;
        useBoxRender?: boolean;
        useColumns?: boolean;
        customCommit?: CustomCommitCallback;
    };

    const props = withDefaults(defineProps<Props>(), {
        beforeSaveCode: async (): Promise<void> => {
            return new Promise<void>((resolve) => {
                resolve();
            });
        },
        codeId: undefined,
        codeSetName: undefined,
        codeTemplateValues: undefined,
        computedFields: () => [],
        customLabels: undefined,
        fieldsToDisable: () => [],
        fieldsToIgnore: () => [],
        formTitle: undefined,
        formTitleClass: undefined,
        multipleCodeSetsToRelease: () => [],
        refCodeChangedHandlers: undefined,
        referableCodesFilters: undefined,
        referableCodesValidators: undefined,
        scopes: () => [],
        submitButtonLabel: 'Save',
        customCommit: undefined
    });

    if (props.codeTemplateValues) {
        codeEditSessionState.setCodeTemplateValues(props.codeTemplateValues);
    }

    const decodedCodeId = computed(() => {
        return props.codeId ? parseInt(decodeIdBase64(props.codeId)) : undefined;
    });

    const selectedCodeId = ref<number>(decodedCodeId.value ?? codeEditSessionState.base.nextNewTmpId());

    function stringValue(field: string): string | undefined {
        const value = codeEditSessionState.code.value?.modified[field];
        return typeof value === 'string' ? value : undefined;
    }

    function nullableStringValue(field: string): string | null | undefined {
        const value = codeEditSessionState.code.value?.modified[field];
        return typeof value === 'string' || value === null ? value : undefined;
    }

    function identityConflictLink(): string {
        const conflictName = codeEditSessionState.code.value?.identityConflict?.name;
        if (!conflictName) {
            return '#';
        }
        return getCodeLink(codeEditSessionState.codeMetadata.value.codeSetName, conflictName);
    }

    const dynamicFilterPredicates = computed((): DynamicFilterPredicates => {
        return codeEditSessionState.fieldDefs.value.flatMap(fieldDef => fieldDef.referenceFilterDynamic
            ? [{
                field: fieldDef.field,
                predicate: fieldDef.referenceFilterDynamic,
                showFiltered: fieldDef.referenceFilterDynamicShowFiltered,
                caseSensitiveCodeRefs: fieldDef.referenceFilterDynamicCaseSensitiveCodeRefs
            }]
            : []);
    });

    const referableCodesFiltered = computed((): Record<string, CodeRefSelectorCode[]> => {
        if (!codeEditSessionState.codeSetName.value) {
            return {};
        }

        let filteredCodes: Record<string, CodeRefSelectorCode[]>;
        if (props.referableCodesFilters) {
            filteredCodes = {};
            Object.keys(codeEditSessionState.referableCodes.value).forEach(key => {
                const filterFunction = props.referableCodesFilters?.[key];
                if (filterFunction) {
                    filteredCodes[key] = codeEditSessionState.referableCodes.value[key].filter(filterFunction);
                } else {
                    filteredCodes[key] = codeEditSessionState.referableCodes.value[key];
                }
            });
        } else {
            filteredCodes = codeEditSessionState.referableCodes.value;
        }

        // Apply dynamic filters on top (mark non-matching options as not selectable, and hidden unless configured to be greyed out)
        const codeFields = codeEditSessionState.code.value?.modified;
        if (!codeFields) {
            return filteredCodes;
        }
        return applyDynamicFilters(filteredCodes, dynamicFilterPredicates.value, codeFields);
    });

    // Hidden options take their reasons with them, so an empty dropdown needs its own explanation
    function dynamicFilterEmptyMessage(fieldDef: FieldDefinition): string {
        const options = referableCodesFiltered.value[fieldDef.field];
        if (!fieldDef.referenceFilterDynamic || !dynamicFilterHidesAllOptions(options)) {
            return '';
        }
        const labels = dynamicFilterBlamedFields(options).map(fieldLabel);
        return noDynamicFilterMatchMessage(labels, fieldDef.referenceFilterDynamicErrorMessage);
    }

    function fieldLabel(field: string): string {
        return codeEditSessionState.fieldDefs.value.find(fieldDef => fieldDef.field === field)?.label || field;
    }

    const referableCodeValidationResults = computed((): Record<string, CodeRefValidationResult> => {
        const validationResults: Record<string, CodeRefValidationResult> = {};
        if (props.referableCodesValidators && codeEditSessionState.code.value && codeEditSessionState.referableCodes.value) {
            Object.keys(codeEditSessionState.referableCodes.value).forEach(key => {
                if (props.referableCodesValidators && Object.keys(props.referableCodesValidators).includes(key) && props.referableCodesValidators[key]) {
                    validationResults[key] = props.referableCodesValidators[key](codeEditSessionState.code.value!.modified, codeEditSessionState.code.value!.original);
                }
            });
        }
        // Dynamic filter violations for currently selected value
        const code = codeEditSessionState.code.value;
        if (code) {
            for (const fieldDef of codeEditSessionState.fieldDefs.value) {
                if (fieldDef.type !== 'CodeRef' || !fieldDef.referenceFilterDynamic) {
                    continue;
                }
                const validationError = code.validationErrors[fieldDef.field];
                if (validationError) {
                    validationResults[fieldDef.field] = {
                        message: validationError.message,
                        messageType: BulmaErrorClass(validationError.type),
                        error: validationError.type === ErrorType.Error
                    };
                }
            }
        }
        return validationResults;
    });

    const titleClass = computed((): Record<string, boolean> => {
        const classes: Record<string, boolean> = {
            'is-2': props.formTitleClass === null,
            'title': true
        };
        if (props.formTitleClass) {
            classes[props.formTitleClass] = true;
        }
        return classes;
    });

    const nameAlias = computed<string | null>(() => {
        const library = libraries.value.find((lib) => lib.name === codeEditSessionState.codeMetadata.value.libraryName);
        if (library) {
            return library.nameAlias;
        }
        return null;
    });

    const currentLibrary = computed(() => {
        return libraries.value.find(lib => lib.name === codeEditSessionState.codeMetadata.value.libraryName);
    });

    const isRelationLibrary = computed(() => {
        return currentLibrary.value?.isRelation === true;
    });

    const isNewRelationCode = computed(() => {
        return isNew() && isRelationLibrary.value;
    });

    const codeRefsInIdentity = computed(() => {
        const filtered = codeEditSessionState.fieldDefs.value.filter(f => f.type === 'CodeRef' && f.includeInIdentity);
        return filtered.slice().sort((a, b) => {
            const seqA = a.sequenceNumber ?? 0;
            const seqB = b.sequenceNumber ?? 0;
            return seqA - seqB;
        });
    });

    function relationLibCodeRefsSelected() {
        if (isNewRelationCode.value) {
            const [codeRefInIdentity1, codeRefInIdentity2] = codeRefsInIdentity.value;
            const library1name = codeRefInIdentity1.refLibraryName || 'Library1';
            const library2name = codeRefInIdentity2.refLibraryName || 'Library2';

            const codeRef1Name = referableCodesFiltered.value[codeRefInIdentity1.field]?.find(c => c.identity === codeEditSessionState.code.value?.modified[codeRefInIdentity1.field])?.name;
            const codeRef2Name = referableCodesFiltered.value[codeRefInIdentity2.field]?.find(c => c.identity === codeEditSessionState.code.value?.modified[codeRefInIdentity2.field])?.name;

            if (codeRef1Name && codeRef2Name) {
                codeEditSessionState.setFieldValue('name', CodeEditSessionState.generateRelationLibCodeName(library1name, codeRef1Name, library2name, codeRef2Name));
                codeEditSessionState.setFieldValue('description', CodeEditSessionState.generateRelationLibCodeDesc(library1name, codeRef1Name, library2name, codeRef2Name));
            } else {
                codeEditSessionState.setFieldValue('name', '');
            }
        }
    }

    function newCtxRequest(): CodeEditContextRequest {
        if (decodedCodeId.value) {
            return {
                codeId: decodedCodeId.value
            };
        }
        if (props.codeSetName) {
            return {
                codeSetName: props.codeSetName
            };
        }
        throw new Error('either codeId or codeSetName must be provided as a prop');
    }

    function isNew() {
        if (decodedCodeId.value) {
            return false;
        }

        if (props.codeSetName) {
            return true;
        }

        throw new Error('either codeId or codeSetName must be provided as a prop');
    }

    onMounted(async () => {
        const librariesPromise = getLibraries(http);
        const ctxRequest = newCtxRequest();
        await codeEditSessionState.loadFromContext(selectedCodeId.value, ctxRequest, isNew());
        libraries.value = await librariesPromise;
        if (!decodedCodeId.value) {
            // Template values should override defaults
            codeEditSessionState.fieldDefs.value.forEach(fieldDefinition => {
                if (fieldDefinition.defaultValue) {
                    fillDefault(fieldDefinition.field);
                }
            });
            codeEditSessionState.applyTemplateValues();

            // Pre-filled references get the same follow-up as the ones the user picks
            const templateValues = props.codeTemplateValues;
            const code = codeEditSessionState.code.value;
            const templateRefFields = codeEditSessionState.fieldDefs.value
                .filter(f => f.type === 'CodeRef' && templateValues && Object.hasOwn(templateValues, f.field))
                .map(f => f.field);
            if (code && templateRefFields.length > 0) {
                templateRefFields.forEach(field => props.refCodeChangedHandlers?.[field]?.(code.modified));
                relationLibCodeRefsSelected();
            }
        }

        const relationAccessPromises
            = codeEditSessionState.relatedCodeSets.value?.map(cs =>
                canEditCode({ libraryName: cs.relationLibraryName, scopes: props.scopes })
                    .then(hasAccess => { return { library: cs.relationLibraryName, hasAccess: hasAccess }; }));

        const results = await Promise.all(relationAccessPromises ?? []);
        results?.forEach((result) => {
            canEditRelation.value[result.library] = result.hasAccess;
        });

        loading.value = false;
        emit('loaded');
    });

    function isSelectorDisabled(fieldDefinition: FieldDefinition): boolean {
        return decodedCodeId.value && fieldDefinition.includeInIdentity || props.fieldsToDisable.includes(fieldDefinition.field);
    }

    function hasOriginalNotInChoices(field: FieldDefinition): boolean {
        return choiceViolation(codeEditSessionState.code.value?.original[field.field], field.choices);
    }

    function clearField(field: string) {
        codeEditSessionState.setFieldValue(field, null);
    }

    function fillDefault(field: string) {
        const fieldDefinition = codeEditSessionState.fieldDefs.value.find(c => c.field === field);
        if (!fieldDefinition || !fieldDefinition.defaultValue) {
            return;
        }
        if (fieldDefinition.type === 'CodeRef') {
            if (referableCodesFiltered.value[field].find(code => code.identity === fieldDefinition.defaultValue)) {
                codeEditSessionState.setFieldValue(field, fieldDefinition.defaultValue);
            } else {
                show.showInfo('Unable to use default value: reference item not found');
            }
        } else if (fieldDefinition.type === 'LibraryRef') {
            if (libraries.value.find(l => l.name === fieldDefinition.defaultValue)) {
                codeEditSessionState.setFieldValue(field, fieldDefinition.defaultValue);
            } else {
                show.showInfo('Unable to use default value: library not found');
            }
        } else {
            codeEditSessionState.setFieldValue(field, fieldDefinition.defaultValue);
        }
    }

    function shouldHideDefaultButton(fieldDefinition: FieldDefinition): boolean {
        if (!fieldDefinition.defaultValue || props.fieldsToDisable.includes(fieldDefinition.field)) {
            return true;
        }
        if (decodedCodeId.value) {
            // We are editing an existing code
            return fieldDefinition.includeInIdentity;
        } else {
            return false;
        }
    }

    function isSvgAttributeType(type: string): boolean {
        return type.toUpperCase() === 'SVG';
    }

    function showMaxLength(fieldDefinition: FieldDefinition) {
        return invalidInputError(fieldDefinition)
            ? codeEditSessionState.regexValidators.value[fieldDefinition.field]?.maxLength
            : undefined;
    }

    /**
     * Returns the regex validation error for the field, if any.
     * @throws {Error} If the current code's modified CodeEditFields object is missing
     * the field defined in the provided fieldDefinition.
     */
    function invalidInputError(fieldDefinition: FieldDefinition) {
        if (!codeEditSessionState.code.value) {
            return;
        }

        const modifiedValue = codeEditSessionState.code.value.modified[fieldDefinition.field];
        if (modifiedValue === undefined) {
            throw new Error(`modified value for attribute ${fieldDefinition.field} is undefined`);
        }
        return codeEditSessionState.regexValidators.value[fieldDefinition.field]?.validate(modifiedValue)?.error;
    }

    function handleRefCodeChange(value: string, field: string) {
        if (!codeEditSessionState.code.value) {
            return;
        }
        codeEditSessionState.setFieldValue(field, value);
        props.refCodeChangedHandlers?.[field]?.(codeEditSessionState.code.value.modified);
        relationLibCodeRefsSelected();
    }

    function filteredReferenceMessage(fieldDefinition: FieldDefinition) {
        return fieldDefinition.referenceFilterStaticErrorMessage
            ? `Warning: reference no longer recommended (${fieldDefinition.referenceFilterStaticErrorMessage})`
            : 'Warning: reference no longer recommended';
    }

    function stringFieldMessageType(fieldDef: FieldDefinition) {
        if (!codeEditSessionState.code.value) {
            return undefined;
        }
        if (fieldDef.field === 'name' && isNewRelationCode.value) {
            return undefined;
        }

        const errType = codeEditSessionState.code.value.validationErrors[fieldDef.field]?.type
            ?? codeEditSessionState.code.value.uniquenessErrors[fieldDef.field]?.type;
        return BulmaErrorClass(errType);
    }

    function stringFieldMessage(fieldDef: FieldDefinition) {
        if (!codeEditSessionState.code.value) {
            return undefined;
        }
        if (fieldDef.field === 'name' && isNewRelationCode.value) {
            return undefined;
        }

        return codeEditSessionState.code.value.validationErrors[fieldDef.field]?.message
            ?? codeEditSessionState.code.value.uniquenessErrors[fieldDef.field]?.message
            ?? '';
    }

    function relationCodeErrorType(code: CodeEditSessionItem): string {
        const allErrors = Object.values({ ...code.validationErrors, ...code.uniquenessErrors });

        let worstType = allErrors[0]?.type;

        if (allErrors.some(e => e.type === ErrorType.Error)) {
            worstType = ErrorType.Error;
        } else if (allErrors.some(e => e.type === ErrorType.Warning)) {
            worstType = ErrorType.Warning;
        }

        return BulmaErrorClass(worstType);
    }

    function relationCodeErrors(code: CodeEditSessionItem): string[] {
        const allErrors = { ...code.validationErrors, ...code.uniquenessErrors };
        return Object.values(allErrors).map(e => e.message);
    }

    function noRelationAccessMessage(libraryName: string): string | null {
        return canEditRelation.value[libraryName]
            ? null
            : `Requires edit access to library '${libraryName}'`;
    }

    function onSaveProgressSuccess(payload: BatchDocumentCode[]) {
        emit('refresh', payload);
    }

    function onSaveProgressClose() {
        emit('cancel');
    }

    const activeMarkdownEdit: Ref<FieldDefinition | null> = ref(null);

    function openPreview(fieldDef: FieldDefinition) {
        activeMarkdownEdit.value = fieldDef;
    }

    function onMarkdownModalDone(newVal: string | null) {
        if (activeMarkdownEdit.value) {
            codeEditSessionState.setFieldValue(activeMarkdownEdit.value?.field, newVal === null ? '' : newVal);
        }
        activeMarkdownEdit.value = null;
    }

    // Lets the page holding the editor ask before replacing it with another item.
    defineExpose({ isDirty: codeEditSessionState.base.isDirty });

</script>
<template>
    <spinner :loading="loading">
        <edit-session
            v-if="codeEditSessionState.code.value"
            :edit-session-state="codeEditSessionState.base"
            :submit-button-label="props.submitButtonLabel"
            :quick-commit="props.quickCommit"
            :use-release-chain="false"
            :can-edit-code-options="{libraryName: props.library, scopes: props.scopes}"
            :before-save-code="async () => {await props.beforeSaveCode(codeEditSessionState.code.value!.modified, codeEditSessionState.referableCodes.value)}"
            :custom-commit="props.customCommit"
            :use-box-render="props.useBoxRender"
            @refresh="onSaveProgressSuccess"
            @cancel="onSaveProgressClose">
            <template #default>
                <form
                    v-if="!loading">
                    <h2
                        v-if="formTitle"
                        :class="titleClass">
                        {{ formTitle }}
                    </h2>
                    <b-notification
                        v-if="codeEditSessionState.code.value.identityConflict"
                        type="is-danger is-light"
                        :closable="false"
                        has-icon
                        role="alert">
                        <p>
                            <b v-if="codeEditSessionState.code.value.identityConflict.isValid">This item already exists!</b>
                            <b v-else>This item already exists, but is invalid!</b>
                        </p>
                        <p>
                            {{ codeEditSessionState.code.value.identityConflict.message }}<br>
                            <a :href="identityConflictLink()">Navigate to existing item </a>
                        </p>
                    </b-notification>
                    <div :class="useColumns ? 'columns-wrapper' : null">
                        <div
                            v-for="fieldDef in codeEditSessionState.fieldDefs.value"
                            :key="fieldDef.field">
                            <div
                                v-if="!isHidden(fieldDef) && !fieldsToIgnore.includes(fieldDef.field) && !isSvgAttributeType(fieldDef.type)"
                                class="field is-horizontal">
                                <label class="field-label label pad-top">
                                    <span>{{
                                        fieldDef.label === 'Name' ? getNameLabel(nameAlias) : fieldDef.label
                                    }}:</span>
                                    <b-tooltip
                                        v-if="fieldDef.field !== 'name' && fieldDef.required"
                                        label="Required"
                                        position="is-top">
                                        <span class="has-text-danger">*</span>
                                    </b-tooltip>
                                    <span
                                        v-else-if="fieldDef.field === 'name' && fieldDef.required"
                                        class="has-text-danger">*</span>
                                    <span v-else>&nbsp;</span>
                                </label>
                                <div
                                    :class="(useColumns ? 'field-body-column' : 'field-body') + ' has-margin-top field is-grouped has-addons'">
                                    <div
                                        v-if="fieldDef.choices && fieldDef.choices.length > 0"
                                        class="control is-expanded">
                                        <b-field
                                            :type="BulmaErrorClass(codeEditSessionState.code.value.validationErrors[fieldDef.field]?.type)"
                                            :message="codeEditSessionState.code.value.validationErrors[fieldDef.field]?.message">
                                            <b-select
                                                v-model="codeEditSessionState.code.value.modified[fieldDef.field]"
                                                expanded>
                                                <option
                                                    v-if="!fieldDef.required"
                                                    :value="null" />
                                                <option
                                                    v-if="hasOriginalNotInChoices(fieldDef)"
                                                    :value="codeEditSessionState.code.value.original[fieldDef.field]">
                                                    {{ codeEditSessionState.code.value.original[fieldDef.field] }}
                                                </option>
                                                <option
                                                    v-for="choice in fieldDef.choices"
                                                    :key="String(choice.value)"
                                                    :value="choice.value">
                                                    {{ choice.value }}
                                                </option>
                                            </b-select>
                                        </b-field>
                                    </div>
                                    <div
                                        v-else
                                        class="control is-expanded">
                                        <typed-bool-selector
                                            v-if="fieldDef.type === 'Bool'"
                                            v-model="codeEditSessionState.code.value.modified[fieldDef.field]" />

                                        <template
                                            v-else-if="fieldDef.type === 'CodeRef'">
                                            <code-ref-selector
                                                :disabled="isSelectorDisabled(fieldDef)"
                                                :selected="stringValue(fieldDef.field)"
                                                :code-refs="referableCodesFiltered[fieldDef.field]"
                                                :message="referableCodeValidationResults[fieldDef.field]?.message"
                                                :message-type="referableCodeValidationResults[fieldDef.field]?.messageType"
                                                :invalid-selection-message="filteredReferenceMessage(fieldDef)"
                                                :empty-message="dynamicFilterEmptyMessage(fieldDef)"
                                                @ref-selected="(value: string) => handleRefCodeChange(value, fieldDef.field)" />
                                        </template>

                                        <library-selector
                                            v-else-if="fieldDef.type === 'LibraryRef'"
                                            :disabled="isSelectorDisabled(fieldDef)"
                                            :selected="stringValue(fieldDef.field)"
                                            @lib-selected="codeEditSessionState.code.value.modified[fieldDef.field] = $event" />

                                        <date-time-picker
                                            v-else-if="fieldDef.type === 'Date'"
                                            :disabled="isSelectorDisabled(fieldDef)"
                                            :input="nullableStringValue(fieldDef.field)"
                                            :date-only="true"
                                            @dt-picker="codeEditSessionState.code.value.modified[fieldDef.field]= $event" />

                                        <date-time-picker
                                            v-else-if="fieldDef.type === 'DateTime'"
                                            :disabled="isSelectorDisabled(fieldDef)"
                                            :input="nullableStringValue(fieldDef.field)"
                                            :date-only="false"
                                            @dt-picker="codeEditSessionState.code.value.modified[fieldDef.field]= $event" />

                                        <b-field
                                            v-else-if="fieldDef.type === 'String'"
                                            :type="stringFieldMessageType(fieldDef)"
                                            :message="stringFieldMessage(fieldDef)">
                                            <generic-text-input
                                                :model-value="nullableStringValue(fieldDef.field)"
                                                :disabled="fieldDef.field === 'name' && isNewRelationCode ? true : isSelectorDisabled(fieldDef)"
                                                :required="fieldDef.required"
                                                :maxlength="showMaxLength(fieldDef)"
                                                :type="fieldDefinitionFormInputType(fieldDef)"
                                                :placeholder="fieldDef.field === 'name' && isNewRelationCode ? 'Will be generated based on the two reference fields included in identity' : undefined"
                                                :text-area-lines="fieldDef.textAreaLines ?? 0"
                                                :message-type="stringFieldMessageType(fieldDef)"
                                                @update:model-value="codeEditSessionState.setFieldValue(fieldDef.field, $event)" />
                                        </b-field>

                                        <div v-else-if="isSvgAttributeType(fieldDef.type)" />

                                        <b-input
                                            v-else
                                            v-model="codeEditSessionState.code.value.modified[fieldDef.field]"
                                            :disabled="isSelectorDisabled(fieldDef)"
                                            :required="fieldDef.required"
                                            :type="fieldDefinitionFormInputType(fieldDef)"
                                            :step="attributeTypeToStepSize(fieldDef.type)" />
                                    </div>
                                    <b-tooltip
                                        v-if="codeEditSessionState.fieldDefs.value.some(f => f.textAreaLines)"
                                        class="control"
                                        label="Open preview"
                                        :class="{'hide': !fieldDef.textAreaLines || fieldDef.textAreaLines === 0}"
                                        position="is-top">
                                        <b-button
                                            class="control"
                                            @click="openPreview(fieldDef)">
                                            <b-icon
                                                icon="book-open-outline"
                                                size="is-small" />
                                        </b-button>
                                    </b-tooltip>
                                    <b-tooltip
                                        class="control"
                                        label="Reset unsaved change"
                                        :class="{'hide': fieldDef.includeInIdentity || fieldsToDisable.includes(fieldDef.field)}"
                                        position="is-top">
                                        <b-button
                                            class="control"
                                            :disabled="codeEditSessionState.code.value.modified[fieldDef.field] === codeEditSessionState.code.value.original[fieldDef.field]"
                                            @click="codeEditSessionState.resetFieldValue(fieldDef.field)">
                                            <b-icon
                                                icon="restore"
                                                size="is-small" />
                                        </b-button>
                                    </b-tooltip>
                                    <b-tooltip
                                        class="control"
                                        label="Clear field"
                                        :class="{'hide': fieldDef.includeInIdentity || fieldDef.required || fieldsToDisable.includes(fieldDef.field)}"
                                        position="is-top">
                                        <b-button
                                            class="control"
                                            :disabled="codeEditSessionState.code.value.modified[fieldDef.field] === null"
                                            @click="clearField(fieldDef.field)">
                                            <b-icon
                                                icon="eraser"
                                                size="is-small" />
                                        </b-button>
                                    </b-tooltip>
                                    <b-tooltip
                                        :class="{'hide': shouldHideDefaultButton(fieldDef)}"
                                        class="control"
                                        :label="'Use default value: \''+ fieldDef.defaultValue +'\''"
                                        position="is-top">
                                        <b-button
                                            class="control"
                                            @click="fillDefault(fieldDef.field)">
                                            Use default
                                        </b-button>
                                    </b-tooltip>
                                </div>
                            </div>
                        </div>

                        <!-- Relation codes section -->
                        <div
                            v-for="relatedCodeSet in codeEditSessionState.relatedCodeSets.value"
                            :key="relatedCodeSet.relationLibCodeSetName"
                            class="field is-horizontal mt-4">
                            <label class="field-label label pad-top">
                                <span>{{ relatedCodeSet.treatAsAttributeLabel }}:</span>
                            </label>
                            <div :class="(useColumns ? 'field-body-column' : 'field-body') + ' has-margin-top'">
                                <div class="is-flex is-flex-direction-column relation-codes-container">
                                    <template
                                        v-for="code in codeEditSessionState.getSortedDisplayableRelationCodes(relatedCodeSet.relationLibCodeSetName)"
                                        :key="`${relatedCodeSet.relationLibCodeSetName}-${code.metadata.codeId}`">
                                        <div class="is-flex is-align-items-flex-start mb-3">
                                            <b-field
                                                class="is-flex-grow-1 mr-3"
                                                :type="relationCodeErrorType(code)"
                                                :message="relationCodeErrors(code).join(', ')">
                                                <div class="has-background-white-ter px-2 py-2 relation-code-box">
                                                    <span
                                                        :class="{'has-text-grey-light': codeEditSessionState.isPendingRemoval(code)}">
                                                        {{ codeEditSessionState.getRelationCodeDisplayLabel(code, relatedCodeSet.relationLibCodeSetName) }}
                                                    </span>
                                                </div>
                                            </b-field>
                                            <div class="relation-code-buttons">
                                                <!-- Restore button for codes pending removal -->
                                                <b-button
                                                    v-if="codeEditSessionState.isPendingRemoval(code)"
                                                    class="control"
                                                    @click="code.modified.isValid = true">
                                                    <b-icon
                                                        icon="restore"
                                                        size="is-small" />
                                                </b-button>
                                                <!-- Eraser for codes added in session -->
                                                <b-button
                                                    v-else-if="codeEditSessionState.isAddedInSession(code)"
                                                    class="control"
                                                    @click="codeEditSessionState.removeRelationCode(
                                                        code,
                                                        relatedCodeSet.relationLibraryName,
                                                        relatedCodeSet.relationLibCodeSetName,
                                                    )">
                                                    <b-icon
                                                        icon="eraser"
                                                        size="is-small" />
                                                </b-button>
                                                <!-- Trash for existing codes -->
                                                <b-button
                                                    v-else
                                                    :disabled="!canEditRelation[relatedCodeSet.relationLibraryName]"
                                                    :title="noRelationAccessMessage(relatedCodeSet.relationLibraryName)"
                                                    class="control"
                                                    @click="codeEditSessionState.removeRelationCode(
                                                        code,
                                                        relatedCodeSet.relationLibraryName,
                                                        relatedCodeSet.relationLibCodeSetName,
                                                    )">
                                                    <b-icon
                                                        icon="delete"
                                                        size="is-small" />
                                                </b-button>
                                            </div>
                                        </div>
                                    </template>
                                    <p
                                        v-if="codeEditSessionState.getSortedDisplayableRelationCodes(relatedCodeSet.relationLibCodeSetName).length === 0"
                                        class="has-text-grey-light is-size-6 has-background-white-ter px-2 py-2 mb-3 relation-code-box">
                                        No items
                                    </p>
                                    <code-ref-selector
                                        :key="relationSelectorKeys[relatedCodeSet.relationLibCodeSetName] ?? 0"
                                        class="relation-code-selector"
                                        :disabled="!canEditRelation[relatedCodeSet.relationLibraryName]"
                                        :placeholder-override="noRelationAccessMessage(relatedCodeSet.relationLibraryName)"
                                        :title="noRelationAccessMessage(relatedCodeSet.relationLibraryName)"
                                        :code-refs="codeEditSessionState.getAvailableCodeRefsForSelector(relatedCodeSet.relationLibCodeSetName, relatedCodeSet.relationLibAttributeName)"
                                        @ref-selected="(value: string) => {
                                            codeEditSessionState.addCodeToRelationLibrary(
                                                relatedCodeSet.relationLibraryName,
                                                relatedCodeSet.relationLibCodeSetName,
                                                value,
                                                relatedCodeSet.targetLibraryName,
                                            );
                                            relationSelectorKeys[relatedCodeSet.relationLibCodeSetName] = (relationSelectorKeys[relatedCodeSet.relationLibCodeSetName] ?? 0) + 1;
                                        }" />
                                </div>
                            </div>
                        </div>
                        <div
                            v-for="(field, i) in computedFields(codeEditSessionState.code.value.modified)"
                            :key="`${i}-${field.value}`">
                            <div class="field is-horizontal">
                                <label class="field-label label pad-top">
                                    <span>{{ field.label }}:</span>
                                </label>
                                <div class="field-body-column has-margin-top field is-grouped has-addons">
                                    <div class="control is-expanded">
                                        <b-input
                                            :model-value="field.hasValue ? field.value : '<null>'"
                                            disabled />
                                    </div>
                                    <b-tooltip
                                        class="control"
                                        :label="'Copy value'"
                                        position="is-top">
                                        <b-button
                                            class="button"
                                            :disabled="!field.hasValue"
                                            @click="copyToClipBoard.copyTextToClipboard(field.value ?? '')">
                                            <b-icon
                                                icon="content-copy"
                                                size="is-small" />
                                        </b-button>
                                    </b-tooltip>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
                <markdown-editor-modal
                    v-if="activeMarkdownEdit"
                    :value="codeEditSessionState.code.value.modified[activeMarkdownEdit.field]?.toString() ?? ''"
                    :show-preview="activeMarkdownEdit.isMarkdown"
                    @reset="activeMarkdownEdit=null"
                    @done="onMarkdownModalDone" />
            </template>
        </edit-session>
    </spinner>
</template>

<style scoped>
.hide {
    visibility: hidden;
}

.pad-top {
    padding-top: 15px;
}

.columns-wrapper {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    height: 50vh;
    padding-right: 5px;
}

.field-body-column {
    display: flex;
    flex-basis: 0;
    flex-grow: 2;
    flex-shrink: 1;
}

.relation-codes-container {
    max-width: 550px;
    width: 100%;
}

.relation-code-box {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
}
</style>
